import { useCallback, useEffect, useState } from "react";
import { getApiMode } from "../api/config.ts";
import { SAVED_CHANGED, idbStore } from "./store.ts";
import type { SavedSpot } from "./store.ts";

/** Saving needs the live API (fixtures have no network to cache), IndexedDB, the Cache API and a worker. */
export const offlineSupported = (): boolean =>
  e2eOfflineButton() ||
  (getApiMode() === "live" &&
    typeof indexedDB !== "undefined" &&
    typeof caches !== "undefined" &&
    "serviceWorker" in navigator);

/** E2E only (`VITE_E2E=1`, folded away in a live build): lets layout specs show the offline button. */
function e2eOfflineButton(): boolean {
  if (import.meta.env.VITE_E2E !== "1") return false;
  try {
    return localStorage.getItem("divespots.e2e.offline") === "1";
  } catch {
    return false;
  }
}

/** The saved spots, kept in step with saves and removals made anywhere in the app. */
export function useSavedSpots(): { spots: SavedSpot[]; loaded: boolean } {
  const [spots, setSpots] = useState<SavedSpot[]>([]);
  const [loaded, setLoaded] = useState(false);
  const refresh = useCallback(() => {
    if (!offlineSupported()) return setLoaded(true);
    idbStore
      .list()
      .then(setSpots)
      .catch(() => setSpots([]))
      .finally(() => setLoaded(true));
  }, []);
  useEffect(() => {
    refresh();
    window.addEventListener(SAVED_CHANGED, refresh);
    return () => window.removeEventListener(SAVED_CHANGED, refresh);
  }, [refresh]);
  return { spots, loaded };
}
